import React, { useContext, useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { connectSideNav } from 'shared/hoc/withSideNav';
import connectRoutes from 'shared/hoc/withRoutes';
import { ticketCreateCheck, ticketEditCheck, ticketViewCheck } from 'shared/api/support';
import { FilterContext, prepareFilters } from 'shared/hoc/withFilters';
import { processRoutes } from 'shared/utils';
import { MODULES } from 'shared/constants';
import * as routes from 'shared/routes/support';
import TicketCreator from './TicketCreator';
import TicketSearch from './TicketSearch';
import TicketView from './TicketView';
import TicketNav from './TicketNav';
import TicketRelocate from './TicketRelocate';

import TicketFilesView from './TicketFilesView';
import TicketReceivers from './TicketReceivers';
import LegacyTicketSearch from './LegacyTicketSearch';
import TicketTag from './TicketTags';

// Title 'Ticket' so history/favorites correctly label as Tickets
const withSideNav = connectSideNav(MODULES.support);

const withFilters = prepareFilters(MODULES.support);

const withRoutes = connectRoutes([
  {
    RouteComponent: TicketSearch,
    path: routes.viewPath,
    text: 'View Tickets',
    routeToTest: ticketViewCheck,
    key: 'view_tickets',
  },
  {
    RouteComponent: TicketCreator,
    path: routes.createPath,
    text: 'Create Ticket',
    routeToTest: ticketCreateCheck,
  },
  {
    RouteComponent: LegacyTicketSearch,
    path: routes.legacyLookupPath,
    text: 'Legacy Lookup',
    routeToTest: ticketViewCheck,
  },
  {
    text: 'Resources',
    routeToTest: ticketEditCheck,
    path: routes.tagPath,
    groupHead: routes.resourcePath,
  },
  {
    RouteComponent: TicketTag,
    path: routes.tagPath,
    group: routes.resourcePath,
    text: 'Ticket Tags',
    routeToTest: ticketEditCheck,
  },
  {
    // Everything below this is passed to the ticket router.
    // These are filtered out of the side nav because there is no 'path' key.
    // The paths are created dynamically via the getPath function.
    RouteComponent: TicketView,
    text: 'View',
    getPath: routes.getTicketPath,
    exact: true,
  },
  {
    RouteComponent: TicketFilesView,
    text: 'Files',
    getPath: routes.getFilesPath,
  },
  {
    RouteComponent: TicketRelocate,
    text: 'Relocate',
    getPath: routes.getRelocatePath,
    exact: true,
  },
  {
    RouteComponent: TicketReceivers,
    text: 'Receivers',
    getPath: routes.getReceiversPath,
  },
]);

/**
 * @param {Object} props
 * @param {Array<Object>} props.enabledRoutes
 */
function Support(props) {
  const { enabledRoutes } = props;
  const {
    savedFilters,
    getFavoriteFilters,
  } = useContext(FilterContext);

  // if saved filters start as undefined, then get favorite filters
  useEffect(() => {
    if (!savedFilters) {
      getFavoriteFilters();
    }
  }, [savedFilters, getFavoriteFilters]);

  // Routes that depend on ticket id.
  const initialTicketNavRoutes = [];
  const initialStaticRoutes = [];

  const { staticRoutes, navRoutes } = processRoutes(
    enabledRoutes,
    initialStaticRoutes,
    initialTicketNavRoutes,
    savedFilters,
  );

  return (
    <Switch>
      {staticRoutes.map((route) => (
        <Route
          render={() => <route.RouteComponent filterKey={route.key || ''} savedFilters={route.filters || ''} path={route.path} />}
          exact
          key={route.path}
          path={route.path}
        />
      ))}
      <Route
        path={`${routes.viewPath}/:routeId`}
        render={({ location }) => <TicketNav enabledRoutes={navRoutes} location={location} />}
      />
    </Switch>
  );
}

export default {
  RouteComponent: withRoutes(withFilters(withSideNav(Support))),
  module: 'support',
  name: 'Support',
  path: routes.viewPath,
  rootPath: routes.rootPath,
  routeToTest: ticketViewCheck,
};
